// Data layer for the co-pilot quiz. One anon RPC (quiz_bootstrap) returns the
// role corpus with aliases and bands, the city-to-metro corpus with recruiter
// counts, the software chips and the live corpus counts. Aggregates only, no
// PII, and every figure is already published elsewhere on the board.
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface QuizRole {
  slug: string;
  name: string;
  grp: string;
  aliases: string;
  p25: number;
  med: number;
  p75: number;
  n: number;
  live: number;
  above: number;
  anew: number;
}

export interface QuizCity {
  c: string; // city
  r: string; // region
  m: string; // metro label
  n: number; // recruiters in metro
  k: "US" | "CA";
}

export interface ScanExample { c: string; t: string; p: number }
export interface RoleScan {
  fresh: number;
  ex?: ScanExample[];
  top?: { c: string; t: string; p: number; m: string };
}

export interface QuizBootstrap {
  roles: QuizRole[];
  cad: Record<string, { p25: number; med: number; p75: number; n: number }>;
  /** Per-role scan facts; absent on caches older than 2026-08-25. Guard reads. */
  scan?: Record<string, RoleScan>;
  cities: QuizCity[];
  software: { slug: string; n: number }[];
  recruiters: { total: number; metros: number };
  corpus: { read: number; live: number; direct: number };
  /** Live above-market examples shown on the GetStarted rail. */
  ticker?: { title: string; company: string; location: string; pay: number; multiple: string }[];
}

const CACHE_KEY = "af_quiz_bootstrap_v1";
const CACHE_MS = 60 * 60 * 1000;

export function useQuizBootstrap() {
  const [data, setData] = useState<QuizBootstrap | null>(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const retry = useCallback(() => {
    setError(false);
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    let alive = true;
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (raw) {
        const { at, payload } = JSON.parse(raw);
        if (Date.now() - at < CACHE_MS) {
          setData(payload);
          return;
        }
      }
    } catch {
      // cache is best-effort
    }
    supabase.rpc("quiz_bootstrap" as never).then(({ data: d, error: e }) => {
      if (!alive) return;
      if (e || !d) {
        setError(true);
        return;
      }
      setData(d as unknown as QuizBootstrap);
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), payload: d }));
      } catch {
        // storage full is fine
      }
    });
    return () => {
      alive = false;
    };
  }, [attempt]);

  return { data, error, retry };
}

/** Title matcher: display-name contains OR alias contains, five best. The
 *  alias list is what makes "sr acc" land on Senior Accountant. */
export function matchRoles(roles: QuizRole[], q: string): QuizRole[] {
  const needle = (q || "").trim().toUpperCase();
  if (needle.length < 2) return [];
  return roles
    .filter((r) => r.name.toUpperCase().includes(needle) || r.aliases.includes(needle))
    .slice(0, 5);
}

/** City matcher. Ranked: exact city name, then prefix matches with a metro's
 *  own principal city first ("Tor" leads with Toronto, not Ajax), then
 *  contains matches, then the other cities in a metro whose name matches. */
export function matchCities(cities: QuizCity[], q: string): QuizCity[] {
  const needle = (q || "").trim().toUpperCase();
  if (needle.length < 2) return [];
  const scored: { c: QuizCity; s: number; i: number }[] = [];
  cities.forEach((c, i) => {
    const up = c.c.toUpperCase();
    const principal = up === c.m.toUpperCase();
    let s = -1;
    if (up === needle) s = principal ? 0 : 1;
    else if (up.startsWith(needle)) s = principal ? 2 : 3;
    else if (up.includes(needle)) s = 4;
    else if (c.m.toUpperCase().startsWith(needle)) s = 5;
    if (s >= 0) scored.push({ c, s, i });
  });
  scored.sort((x, y) => x.s - y.s || x.i - y.i);
  return scored.slice(0, 6).map((x) => x.c);
}

export function money(v: number | null | undefined, cad = false): string {
  const n = Math.round(Number(v));
  if (!isFinite(n) || n <= 0) return "";
  return "$" + n.toLocaleString("en-US") + (cad ? " CAD" : "");
}

export function num(v: number | null | undefined): string {
  const n = Number(v);
  return isFinite(n) ? n.toLocaleString("en-US") : "";
}

export const TOOL_GROUPS: { group: string; items: [string, string][] }[] = [
  { group: "Core ERP and ledger", items: [["sap", "SAP"], ["oracle", "Oracle"], ["workday_fin", "Workday"], ["netsuite", "NetSuite"], ["quickbooks", "QuickBooks"], ["dynamics", "Dynamics"], ["intacct", "Sage Intacct"], ["xero", "Xero"]] },
  { group: "Close and reconciliation", items: [["blackline", "BlackLine"], ["floqast", "FloQast"], ["workiva", "Workiva"]] },
  { group: "Spend, AP and procurement", items: [["spend_new", "Spend tools"], ["concur", "Concur"], ["coupa", "Coupa"], ["avalara", "Avalara"]] },
  { group: "Payroll and people", items: [["adp", "ADP"], ["paylocity", "Paylocity"], ["paycom", "Paycom"], ["paychex", "Paychex"], ["gusto", "Gusto"]] },
  { group: "Planning and analysis", items: [["powerbi", "Power BI"], ["tableau", "Tableau"], ["hyperion", "Hyperion"], ["onestream", "OneStream"], ["alteryx", "Alteryx"], ["anaplan", "Anaplan"]] },
  { group: "Tax and audit", items: [["cch", "CCH"], ["tax_prep", "Tax prep suites"], ["caseware", "CaseWare"]] },
  { group: "AI in the job description", items: [["copilot", "Copilot"], ["claude", "Claude"], ["chatgpt", "ChatGPT"], ["gemini", "Gemini"]] },
];

// ---------------------------------------------------------------------------
// Completion profile. Written once at quiz submit, read on return visits to
// /get-started to render the live "since you set up" surface. Local storage
// only: the surface is anonymous by design, so personalization never needs an
// account and no per-email endpoint exists to enumerate. Numbers stored here
// are the baseline the return visit diffs against.
export const PROFILE_KEY = "af_copilot_profile_v1";

export interface CopilotProfile {
  v: 1;
  at: string; // ISO timestamp of submission
  role: { slug: string; name: string } | null;
  city: QuizCity | null;
  setup: string | null; // onsite | hybrid | remote | any
  base: {
    live: number;   // live postings in the role at submission
    med: number;    // median shown to them (already CAD when isCA)
    above: number;  // above-market roles open at submission
    isCA: boolean;
  } | null;
}

export function readProfile(): CopilotProfile | null {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return p && p.v === 1 ? (p as CopilotProfile) : null;
  } catch {
    return null;
  }
}

export function writeProfile(p: CopilotProfile): void {
  try {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(p));
  } catch {
    // storage unavailable: the return surface simply never appears
  }
}
